import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  type DocumentAction,
  downloadDocumentIsdoc,
  downloadDocumentIsdocx,
  downloadDocumentPdf,
  downloadDocumentUbl,
  downloadDocumentXml,
  downloadFile,
  useArchiveDocument,
  useCan,
  useCompanies,
  useDeleteDraft,
  useDocument,
  useDocumentAction,
  useDocumentApproval,
  useDocumentArchive,
  useEmails,
  useFeatureFlag,
  useUblCheck,
  useUpdateDocumentNote,
  useVerifyDocument,
} from '../api/hooks';
import type { Document, DocumentLink, RelatedDoc } from '../api/types';
import { Comments } from '../components/Comments';
import { I } from '../components/Icons';
import { StockTraceSection } from '../components/StockTrace';
import { Btn, Empty, H2, Pill, Row, Surface, Tag, TextareaField } from '../components/ui';
import { useT } from '../i18n';
import {
  isPayable,
  isReceived,
  LINK_LABEL,
  METHODS,
  relatedLabel,
  SOURCE_LABEL,
  STATUS,
  TYPES,
} from '../lib/doc';
import { daysBetween, dt, money, plural, todayIso } from '../lib/format';
import { useCompanyFilter } from '../state/company';
import { useTopbar } from '../state/topbar';
import { useUi } from '../state/ui';
import { PaymentSheet } from './PaymentSheet';
import { SendSheet } from './SendSheet';

/** typy, které umí ISDOC (daňové doklady a zálohy) */
const ISDOC_TYPES = new Set(['FV', 'DB', 'VR', 'ZF', 'PF', 'DD']);

export function DocumentDetail() {
  const { id = '' } = useParams();
  const { data: d, error } = useDocument(id);
  const t = useT();
  useTopbar(d ? TYPES[d.type].label : t('doc.new'), { back: '/doklady' });
  const { data: companies = [] } = useCompanies();
  const navigate = useNavigate();
  const { openSheet, closeSheet, toast } = useUi();
  const action = useDocumentAction(id);
  const removeDraft = useDeleteDraft();
  const { activeCompanyId } = useCompanyFilter();
  const saveNote = useUpdateDocumentNote(id);
  const eInvoicing = useFeatureFlag('e_invoicing', activeCompanyId);
  const peppol = useFeatureFlag('peppol', activeCompanyId);
  const { data: ublCheck } = useUblCheck(id, !!d && d.status !== 'draft' && (eInvoicing || peppol));
  const { data: approval } = useDocumentApproval(id);
  const { data: archiveData } = useDocumentArchive(id, !!d && d.status !== 'draft');
  const archiveDoc = useArchiveDocument(id);
  const { data: emailsData } = useEmails(d?.companyId ?? '', 'document', id);
  const [busy, setBusy] = useState<'pdf' | 'xml' | 'isdoc' | 'isdocx' | 'ubl' | null>(null);
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const isDraft = d?.status === 'draft';
  const { data: verify } = useVerifyDocument(id, !!d && !isDraft && !!d.contentHash);
  const access = useCan(d?.companyId);
  const allow = (key: Parameters<typeof access.can>[0]) => !access.loaded || access.can(key);

  if (error instanceof ApiError && error.status === 404) {
    return (
      <Surface className="mt">
        <Empty>Doklad nenalezen.</Empty>
      </Surface>
    );
  }
  if (!d) return null;

  const S = companies.find((s) => s.id === d.companyId);
  const vatPayer = d.vatPayer;
  const dl = d.type === 'DL';
  /** doklady bez platby: nabídka, objednávky, příjemka, výdejka, interní */
  const noPay = !isPayable(d.type) && d.type !== 'DD';
  const received = isReceived(d.type);
  const number = d.number ?? 'Koncept';
  const cur = d.currency ?? 'CZK';
  const homeCur = S?.currency ?? 'CZK';
  const fmt = (v: string | number) => money(v, cur);
  const s = d.status;
  const overdueDays = s === 'overdue' ? daysBetween(d.dueAt, todayIso()) : 0;
  const canPay =
    !noPay &&
    !isDraft &&
    !['paid', 'settled', 'cancelled'].includes(s) &&
    d.type !== 'DD' &&
    allow('payments:write');
  const pcs = d.items.reduce((a, x) => a + Number(x.quantity), 0);
  const links: DocumentLink[] = d.links ?? [];
  const hasChild = (t: string) =>
    d.relatedDocuments.some((x) => x.type === t) ||
    links.some(
      (l) => l.direction === 'from' && l.document.type === t && l.document.status !== 'cancelled',
    );
  /** samostatný dodací list (bez faktury nad sebou) jde vyfakturovat */
  const standaloneDl =
    dl &&
    !links.some(
      (l) => l.direction === 'to' && (l.document.type === 'FV' || l.document.type === 'PF'),
    );
  const confirmable = dl || d.type === 'NA' || d.type === 'OP' || d.type === 'OV';

  /** Stažení souboru ze serveru: PDF i XML se chovají stejně, hlásí se toastem. */
  const grab = async (kind: 'pdf' | 'xml' | 'isdoc' | 'isdocx' | 'ubl') => {
    setBusy(kind);
    try {
      const fn =
        kind === 'pdf'
          ? downloadDocumentPdf
          : kind === 'xml'
            ? downloadDocumentXml
            : kind === 'isdoc'
              ? downloadDocumentIsdoc
              : kind === 'isdocx'
                ? downloadDocumentIsdocx
                : downloadDocumentUbl;
      const name = await fn(d.id, number);
      toast(`Staženo jako ${name}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Soubor se nepodařilo stáhnout');
    } finally {
      setBusy(null);
    }
  };

  const run = (a: DocumentAction, done: (doc: Document) => void) =>
    action.mutate(a, {
      onSuccess: (r) => {
        if (r.approvalRequired) {
          toast('Doklad čeká na schválení – vystaví se po schválení');
          return;
        }
        done(r.document);
      },
      onError: (err) => toast(err.message),
    });
  const derive = (a: DocumentAction) =>
    run(a, (doc) => {
      navigate(`/doklady/${doc.id}`);
      toast(`${TYPES[doc.type].label} ${doc.number} ${TYPES[doc.type].done}`);
    });

  const confirmCancel = () =>
    openSheet(
      <>
        <h3>Stornovat {d.number}?</h3>
        <p className="muted mb">
          Doklad zůstane v evidenci jako stornovaný, jeho číslo se znovu nepoužije a do POHODY se
          nepošle.
        </p>
        <Btn
          variant="danger-solid"
          full
          onClick={() => {
            closeSheet();
            run('cancel', (doc) => toast(`${doc.number} stornováno`));
          }}
        >
          Stornovat
        </Btn>
        <Btn full onClick={closeSheet}>
          Zpět
        </Btn>
      </>,
    );

  const relRow = (x: RelatedDoc, label: string) => (
    <Row key={x.id} onClick={() => navigate(`/doklady/${x.id}`)}>
      <div className="grow">
        <div className="k">{label}</div>
        <div className="l1">{x.number ?? 'Koncept'}</div>
      </div>
    </Row>
  );
  /** vazby: `to` = na co doklad navazuje, `from` = co navazuje na něj */
  const linkRow = (l: DocumentLink) =>
    relRow(
      l.document,
      l.direction === 'to'
        ? (LINK_LABEL[l.kind] ?? 'Navazuje na')
        : `${TYPES[l.document.type].label} k tomuto dokladu`,
    );

  const confirmDelete = () =>
    openSheet(
      <>
        <h3>Smazat koncept?</h3>
        <p className="muted mb">Koncept nemá číslo, po smazání po něm nic nezůstane.</p>
        <Btn
          variant="danger-solid"
          full
          onClick={() => {
            closeSheet();
            removeDraft.mutate(d.id, {
              onSuccess: () => {
                navigate('/doklady', { replace: true });
                toast('Koncept smazán');
              },
              onError: (err) => toast(err.message),
            });
          }}
        >
          Smazat
        </Btn>
        <Btn full onClick={closeSheet}>
          Zpět
        </Btn>
      </>,
    );

  return (
    <>
      <section className={`dochead ${d.cancelledAt ? 'cancelled' : ''}`}>
        <div className="dh-num">{number}</div>
        <div className="dh-amt">
          {dl ? `${pcs} ks` : `${d.type === 'DB' ? '−' : ''}${fmt(d.totalAmount)}`}
          {!dl && cur !== homeCur && d.totalAmountHome && (
            <div className="l2">
              <span>
                {money(d.totalAmountHome, homeCur)} · kurz {d.exchangeRate}
                {d.exchangeRateSource ? ` (${d.exchangeRateSource.toUpperCase()})` : ''}
              </span>
            </div>
          )}
        </div>
        <div className="dh-st">
          <Pill kind={s}>{STATUS[s]}</Pill>
          {(s === 'partial' || s === 'overdue') && (
            <span className="muted">zbývá {fmt(d.remaining)}</span>
          )}
          {overdueDays > 0 && (
            <span className="bad">
              {overdueDays} {plural(overdueDays, ['den', 'dny', 'dní'])} po splatnosti
            </span>
          )}
          {s === 'confirmed' && (
            <span className="muted">{dt(d.confirmedAt?.slice(0, 10) ?? d.issuedAt)}</span>
          )}
          {d.importedFromPohoda && <Tag>z POHODY</Tag>}
          {d.externalNumber && <Tag>č. dodavatele {d.externalNumber}</Tag>}
          {d.documentKind === 'tax_invoice' && <Tag>Tax Invoice</Tag>}
          {d.documentKind === 'simplified_tax_invoice' && <Tag>Simplified Tax Invoice</Tag>}
          {d.intercompany && <Tag>intercompany</Tag>}
          {verify?.valid === true && <Tag ok>otisk ověřen</Tag>}
          {verify?.valid === false && (
            <span className="bad">otisk nesedí – doklad byl změněn mimo aplikaci</span>
          )}
        </div>
      </section>
      {isDraft && (
        <p className="warn-box">
          Koncept: nemá číslo, nehýbe skladem a jde upravit. Číslo dostane až při vystavení.
        </p>
      )}
      {d.purchaseDoc?.state === 'missing' && (
        <div className="warn-box purchase-box" data-purchase-missing>
          <span className="purchase-miss" aria-hidden="true">
            !
          </span>
          <div>
            <b>Chybí nákupní doklad</b>
            <ul>
              {d.purchaseDoc.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <Surface>
        <Row onClick={() => navigate(`/vice/kontakty/${d.contactId}`)}>
          <div className="grow">
            <div className="k">{received ? 'Dodavatel' : 'Odběratel'}</div>
            <div className="l1">{d.customerName}</div>
            {d.customerIco && (
              <div className="l2">
                <span>IČO {d.customerIco}</span>
                {d.customerDic && <span>DIČ {d.customerDic}</span>}
              </div>
            )}
          </div>
        </Row>
        {noPay ? (
          <div className="row grid3">
            <div>
              <div className="k">Vystaveno</div>
              <div>{dt(d.issuedAt)}</div>
            </div>
            <div>
              <div className="k">Položek</div>
              <div>{d.items.length}</div>
            </div>
            <div>
              <div className="k">Vystavil</div>
              <div>{S?.shortName ?? ''}</div>
            </div>
          </div>
        ) : (
          <>
            <div className="row grid3">
              <div>
                <div className="k">Vystaveno</div>
                <div>{dt(d.issuedAt)}</div>
              </div>
              <div>
                <div className="k">DUZP</div>
                <div>{dt(d.taxDate)}</div>
              </div>
              <div>
                <div className="k">Splatnost</div>
                <div className={s === 'overdue' ? 'bad' : ''}>{dt(d.dueAt)}</div>
              </div>
            </div>
            <div className="row grid3">
              <div>
                <div className="k">Úhrada</div>
                <div>{METHODS[d.paymentMethod]}</div>
              </div>
              <div>
                <div className="k">Var. symbol</div>
                <div>{d.variableSymbol}</div>
              </div>
              <div>
                <div className="k">Vystavil</div>
                <div>{S?.shortName ?? ''}</div>
              </div>
            </div>
          </>
        )}
        {links.length
          ? links.map(linkRow)
          : [
              ...(d.relatedDocument ? [relRow(d.relatedDocument, relatedLabel(d.type))] : []),
              ...d.relatedDocuments.map((x) => relRow(x, TYPES[x.type].label)),
            ]}
      </Surface>

      <H2>{t('doc.items')}</H2>
      <Surface>
        {d.items.map((x) => (
          <div className="row" key={x.id}>
            <div className="grow">
              <div className="l1">{x.text}</div>
              {x.description && <div className="l2 item-desc-l">{x.description}</div>}
              {!dl && (
                <div className="l2">
                  <span>
                    {x.quantity} {x.unit} × {fmt(x.unitPrice)}
                  </span>
                  {vatPayer && <span>DPH {x.vatRate} %</span>}
                </div>
              )}
            </div>
            <div className="amt">{dl ? `${x.quantity} ${x.unit}` : fmt(x.lineTotal)}</div>
          </div>
        ))}
        {!dl && (
          <div className="row sums">
            {vatPayer && (
              <>
                <div className="sl">
                  <span>Základ</span>
                  <span>{fmt(d.baseAmount)}</span>
                </div>
                <div className="sl">
                  <span>DPH</span>
                  <span>{fmt(d.vatAmount)}</span>
                </div>
              </>
            )}
            <div className="sl tot">
              <span>Celkem</span>
              <span>{fmt(d.totalAmount)}</span>
            </div>
          </div>
        )}
        {d.legalTexts?.length ? (
          <div className="row">
            <div className="grow muted">
              {d.legalTexts.map((t) => (
                <div key={t}>{t}</div>
              ))}
            </div>
          </div>
        ) : null}
      </Surface>

      {!noPay && !isDraft && (
        <>
          <H2>{t('doc.payments')}</H2>
          <Surface>
            {d.payments.length ? (
              d.payments.map((p) => (
                <div className="row" key={p.id}>
                  <span className="mi">{I[p.method]}</span>
                  <div className="grow">
                    <div className="l1">{METHODS[p.method]}</div>
                    <div className="l2">
                      <span>{dt(p.date)}</span>
                      <span>{p.payerName}</span>
                      <span>{SOURCE_LABEL[p.source]}</span>
                    </div>
                    {p.note && <div className="l2">{p.note}</div>}
                  </div>
                  <div className={`amt ${Number(p.amount) < 0 ? 'bad' : 'ok'}`}>
                    {Number(p.amount) < 0 ? '−' : '+'}
                    {fmt(Math.abs(Number(p.amount)))}
                  </div>
                </div>
              ))
            ) : (
              <Empty>
                Zatím žádná platba.
                {canPay ? ' Jakmile dorazí peníze, zapiš je tady nebo je spáruj z výpisu.' : ''}
              </Empty>
            )}
            {canPay && (
              <div className="row">
                <Btn
                  variant="primary"
                  full
                  onClick={() => openSheet(<PaymentSheet doc={d} onDone={() => undefined} />)}
                >
                  {d.type === 'DB' ? 'Zaznamenat vrácení' : 'Zaznamenat úhradu'}
                </Btn>
              </div>
            )}
          </Surface>
        </>
      )}

      <div className="actions">
        {isDraft && allow('documents:issue') && (
          <>
            <Btn
              variant="primary"
              onClick={() =>
                run('issue', (doc) =>
                  toast(`${TYPES[doc.type].label} ${doc.number} ${TYPES[doc.type].done}`),
                )
              }
            >
              {t('doc.issue')}
            </Btn>
            <Btn onClick={() => navigate(`/novy?draft=${d.id}`)}>{t('doc.edit')}</Btn>
            <Btn variant="danger" onClick={confirmDelete}>
              {t('doc.deleteDraft')}
            </Btn>
          </>
        )}
        {!isDraft &&
          confirmable &&
          !d.confirmedAt &&
          !d.cancelledAt &&
          allow('documents:issue') && (
            <Btn
              variant="primary"
              onClick={() =>
                run(dl ? 'confirm-delivery' : 'confirm', (doc) =>
                  toast(dl ? `${doc.number}: převzetí potvrzeno` : `${doc.number} potvrzeno`),
                )
              }
            >
              {dl ? 'Potvrdit převzetí' : d.type === 'NA' ? 'Nabídka přijata' : 'Potvrdit'}
            </Btn>
          )}
        {!isDraft && (
          <Btn variant="primary" loading={busy === 'pdf'} onClick={() => grab('pdf')}>
            {t('doc.pdf')}
          </Btn>
        )}
        {/* dobropis a dodací list hned vedle PDF – k faktuře je potřeba nejčastěji */}
        {d.type === 'FV' && !isDraft && !d.cancelledAt && allow('documents:issue') && (
          <Btn className="btn-credit" onClick={() => derive('credit-note')}>
            Vystavit dobropis
          </Btn>
        )}
        {(d.type === 'FV' || d.type === 'PF') &&
          !isDraft &&
          !d.cancelledAt &&
          allow('documents:issue') && (
            <Btn onClick={() => derive('delivery-note')}>Dodací list</Btn>
          )}
        <Btn onClick={() => navigate(`/doklady/${d.id}/nahled`)}>{t('doc.preview')}</Btn>
        {!isDraft && !d.cancelledAt && allow('documents:issue') && (
          <Btn
            variant={d.sentAt ? 'default' : 'primary'}
            onClick={() =>
              openSheet(<SendSheet doc={d} mode="send" defaultTo="" onDone={() => undefined} />)
            }
          >
            {t('doc.sendEmail')}
          </Btn>
        )}
        {!isDraft && !d.cancelledAt && !d.sentAt && allow('documents:issue') && (
          <Btn onClick={() => run('sent', (doc) => toast(`${doc.number} označeno jako odeslané`))}>
            {t('doc.markSent')}
          </Btn>
        )}
        {!isDraft &&
          (s === 'overdue' || s === 'unpaid' || s === 'partial' || s === 'sent') &&
          allow('documents:issue') && (
            <Btn
              onClick={() =>
                openSheet(<SendSheet doc={d} mode="remind" defaultTo="" onDone={() => undefined} />)
              }
            >
              {t('doc.remind')}
              {d.reminderCount ? ` (${d.reminderCount}.)` : ''}
            </Btn>
          )}
        {d.type === 'ZF' && s === 'paid' && !hasChild('DD') && allow('documents:issue') && (
          <Btn onClick={() => derive('tax-document')}>Daňový doklad k platbě</Btn>
        )}
        {d.type === 'PF' && s === 'paid' && !hasChild('FV') && allow('documents:issue') && (
          <Btn onClick={() => derive('invoice-from-proforma')}>Vystavit fakturu</Btn>
        )}
        {(d.type === 'OP' || standaloneDl) &&
          !isDraft &&
          !d.cancelledAt &&
          !hasChild('FV') &&
          allow('documents:issue') && <Btn onClick={() => derive('invoice')}>Vystavit fakturu</Btn>}
        {!noPay && !isDraft && TYPES[d.type].pohoda && allow('exports:read') && (
          <Btn loading={busy === 'xml'} onClick={() => grab('xml')}>
            XML pro POHODU
          </Btn>
        )}
        {!isDraft && !d.cancelledAt && ISDOC_TYPES.has(d.type) && allow('exports:read') && (
          <>
            <Btn loading={busy === 'isdoc'} onClick={() => grab('isdoc')}>
              ISDOC
            </Btn>
            <Btn loading={busy === 'isdocx'} onClick={() => grab('isdocx')}>
              ISDOCX
            </Btn>
            {eInvoicing && (
              <Btn loading={busy === 'ubl'} onClick={() => grab('ubl')}>
                UBL
              </Btn>
            )}
          </>
        )}
        {!isDraft && !d.cancelledAt && allow('documents:cancel') && (
          <Btn variant="danger" onClick={confirmCancel}>
            Stornovat
          </Btn>
        )}
      </div>
      {isDraft && approval && (
        <Surface className="pad mt">
          <div className="k">Čeká na schválení</div>
          <div className="l1">{approval.reason}</div>
          <div className="l2">
            Požádal {approval.requestedByEmail ?? 'systém'} · {dt(approval.createdAt)} · schvaluje{' '}
            {approval.approverRole}
          </div>
          {allow('approvals:decide') && (
            <Btn sm className="mt" onClick={() => navigate('/vice/schvalovani')}>
              Otevřít schvalování
            </Btn>
          )}
        </Surface>
      )}
      {!isDraft && emailsData && emailsData.emails.length > 0 && (
        <>
          <H2>{t('doc.emails')}</H2>
          <Surface>
            {emailsData.emails.map((m) => (
              <div className="row" key={m.id}>
                <div className="grow">
                  <div className="l1">
                    {m.templateKey === 'reminder' ? 'Upomínka' : 'Doklad'} → {m.to}
                  </div>
                  <div className="l2">
                    {m.status === 'sent'
                      ? `odesláno ${dt(m.sentAt ?? m.createdAt)}`
                      : m.status === 'failed'
                        ? `selhalo: ${m.lastError ?? ''}`
                        : 'čeká na odeslání'}
                  </div>
                </div>
                <Tag ok={m.status === 'sent'}>
                  {m.status === 'sent' ? 'odesláno' : m.status === 'failed' ? 'chyba' : 'čeká'}
                </Tag>
              </div>
            ))}
          </Surface>
        </>
      )}
      {!isDraft && ublCheck && (
        <>
          <H2>PEPPOL / UBL</H2>
          <Surface className="pad">
            {ublCheck.ready ? (
              <div className="l1">
                <Tag ok>připraveno</Tag> Doklad má všechna povinná pole PEPPOL BIS 3.
              </div>
            ) : (
              <>
                <div className="l1">
                  <Tag>chybí {ublCheck.missing.length}</Tag> Pro odeslání přes PEPPOL doplň:
                </div>
                <ul className="l2" data-testid="ubl-missing">
                  {ublCheck.missing.map((m) => (
                    <li key={m.code}>
                      <span className="mono">{m.code}</span> – {m.message}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Surface>
        </>
      )}
      {!isDraft && <StockTraceSection documentId={d.id} currency={S?.currency ?? 'CZK'} />}
      {!isDraft && (
        <>
          <H2>{t('doc.archive')}</H2>
          <Surface>
            {(archiveData?.archives ?? []).map((a) => {
              const valid = archiveData?.integrity.find((i) => i.id === a.id)?.valid;
              return (
                <div className="row" key={a.id}>
                  <div className="grow">
                    <div className="l1">
                      {a.kind.startsWith('original-')
                        ? `Originál od dodavatele (${a.kind.slice(9).toUpperCase()})`
                        : a.kind.toUpperCase()}{' '}
                      · {dt(a.createdAt)} · {Math.round(a.size / 1024)} kB
                    </div>
                    <div className="l2">sha256 {a.sha256.slice(0, 16)}…</div>
                  </div>
                  {valid === false ? <Tag>porušeno</Tag> : <Tag ok>ověřeno</Tag>}
                  <button
                    type="button"
                    className="link"
                    onClick={() =>
                      downloadFile(`/api/documents/${d.id}/archive/${a.id}`).catch((e: Error) =>
                        toast(e.message),
                      )
                    }
                  >
                    stáhnout
                  </button>
                </div>
              );
            })}
            {allow('documents:issue') && (
              <div className="row">
                <Btn
                  sm
                  loading={archiveDoc.isPending}
                  onClick={() =>
                    archiveDoc.mutate(undefined, {
                      onSuccess: () => toast('Archivní kopie uložena'),
                      onError: (e) => toast(e.message),
                    })
                  }
                >
                  Uložit archivní kopii (PDF + ISDOC)
                </Btn>
              </div>
            )}
          </Surface>
        </>
      )}
      <Comments companyId={d.companyId} entityType="document" entityId={d.id} />
      {!isDraft && (
        <>
          <H2>{t('doc.internalNote')}</H2>
          <Surface className="pad">
            {noteDraft === null ? (
              <div className="rowx">
                <div className="grow muted">
                  {d.internalNote || 'Jen pro nás, na doklad se netiskne.'}
                </div>
                {allow('documents:issue') && (
                  <button
                    type="button"
                    className="link"
                    onClick={() => setNoteDraft(d.internalNote ?? '')}
                  >
                    Upravit
                  </button>
                )}
              </div>
            ) : (
              <>
                <TextareaField
                  id="dd-inote"
                  label="Interní poznámka"
                  rows={2}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                />
                <div className="two">
                  <Btn
                    sm
                    variant="primary"
                    loading={saveNote.isPending}
                    onClick={() =>
                      saveNote.mutate(noteDraft, {
                        onSuccess: () => {
                          setNoteDraft(null);
                          toast('Poznámka uložena');
                        },
                        onError: (err) => toast(err.message),
                      })
                    }
                  >
                    Uložit
                  </Btn>
                  <Btn sm onClick={() => setNoteDraft(null)}>
                    Zrušit
                  </Btn>
                </div>
              </>
            )}
          </Surface>
        </>
      )}
      {d.note && (
        <>
          <H2>{t('doc.note')}</H2>
          <Surface className="pad">{d.note}</Surface>
        </>
      )}
    </>
  );
}
